import { faBroom } from '@fortawesome/free-solid-svg-icons';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ProductType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Tabs,
} from '../../components/ui';
import {
  useMyServiceJobsQuery,
  useMyWorkerJobsQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import {
  jobGroup,
  jobItemLabels,
  jobPath,
  jobStatusView,
  windowText,
  type JobGroup,
  type ServiceJob,
} from './serviceJob';

const Row = styled.button`
  ${tw`flex items-center w-full gap-4 p-3 text-left cursor-pointer`}
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card, 12px);
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  &:hover {
    box-shadow: var(--shadow-card-hover);
  }
`;

const Photo = styled.img`
  ${tw`object-cover`}
  width: 80px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--color-bg-secondary);
`;

const Body = styled.div`
  ${tw`flex flex-col flex-1 gap-1`}
  min-width: 0;
`;

const TAB_LABELS: Record<JobGroup, string> = {
  upcoming: 'Upcoming',
  inProgress: 'In progress',
  past: 'Past',
  cancelled: 'Cancelled',
};

/**
 * The customer's jobs (spec 019 W16): Upcoming / In progress / Past /
 * Cancelled, from `myServiceJobs` (every status except unpaid drafts):
 * requests waiting for the worker show under Upcoming, declined and expired
 * ones under Cancelled.
 */
export default function ServiceJobsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<JobGroup>('upcoming');
  const workerJobs = useMyWorkerJobsQuery({
    variables: { date: null },
    fetchPolicy: 'cache-first',
  });
  const query = useMyServiceJobsQuery({
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network',
  });
  const jobs = useMemo(
    () =>
      (query.data?.myServiceJobs ?? []).filter(
        job => job.productType === ProductType.HouseAndVehicleService,
      ),
    [query.data],
  );
  const grouped = useMemo(() => {
    const now = Date.now();
    const groups: Record<JobGroup, ServiceJob[]> = {
      upcoming: [],
      inProgress: [],
      past: [],
      cancelled: [],
    };
    for (const job of jobs) groups[jobGroup(job, now)].push(job);
    const start = (j: ServiceJob) => j.serviceJob?.windowStartAt ?? 0;
    groups.upcoming.sort((a, b) => start(a) - start(b));
    groups.inProgress.sort((a, b) => start(a) - start(b));
    groups.past.sort((a, b) => start(b) - start(a));
    groups.cancelled.sort((a, b) => start(b) - start(a));
    return groups;
  }, [jobs]);
  const list = grouped[tab];

  return (
    <PageContainer width="lg" data-testid="service-jobs">
      <PageHeader
        title="Jobs"
        actions={
          <Button type="button" theme="secondary" text="Book a job" onClick={() => navigate('/')} />
        }
      />
      <Stack $gap={6}>
        {(workerJobs.data?.myWorkerJobs.length ?? 0) > 0 && (
          <Alert
            tone="info"
            title="You also work jobs"
            actions={
              <Button
                type="button"
                size="sm"
                text="Jobs you work"
                onClick={() => navigate('/services/worker')}
              />
            }
          >
            {workerJobs.data!.myWorkerJobs.length} coming jobs as a worker.
          </Alert>
        )}
        <Tabs
          variant="underline"
          value={tab}
          onChange={setTab}
          items={(Object.keys(TAB_LABELS) as JobGroup[]).map(key => ({
            key,
            label: TAB_LABELS[key],
            count: grouped[key].length || undefined,
          }))}
        />
        {query.loading && !query.data ? (
          <LoadingBlock label="Loading your jobs" />
        ) : query.error && !query.data ? (
          <Alert tone="error" title="Could not load your jobs">
            {stayErrorMessage(query.error)}
          </Alert>
        ) : list.length === 0 ? (
          <EmptyState icon={faBroom} title={`No ${TAB_LABELS[tab].toLowerCase()} jobs`} />
        ) : (
          <Stack $gap={4}>
            {list.map(job => {
              const status = jobStatusView(job);
              const info = job.serviceJob;
              const photo =
                job.car?.serviceWorker?.equipmentPhotos[0] ?? job.car?.images?.[0]?.imageUrl;
              return (
                <Row
                  key={job.id}
                  type="button"
                  onClick={() => navigate(jobPath(job.id))}
                  data-testid={`job-row-${job.id}`}
                >
                  {photo ? <Photo src={toPublicS3ObjectUrl(photo)} alt="" /> : <Photo as="div" />}
                  <Body>
                    <Strong>{jobItemLabels(info?.items).join(' + ') || 'Service job'}</Strong>
                    {info && (
                      <Muted $size="sm">
                        {windowText(info.windowStartAt, info.windowEndAt, info.timezone)}
                      </Muted>
                    )}
                    <Muted $size="sm">
                      {[info?.workerName, info?.site?.nickname].filter(Boolean).join(' · ')}
                    </Muted>
                    {info?.fromCalendar && (
                      <Muted $size="sm" data-testid="job-from-calendar">
                        Booked from your calendar
                      </Muted>
                    )}
                  </Body>
                  <Badge tone={status.tone}>{status.label}</Badge>
                </Row>
              );
            })}
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
}
